import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/site/Motion";
import { UkClock } from "@/components/site/Clock";
import { HERO } from "@/data/site";
import { EASE, INTRO_DELAY, scrollToTarget } from "@/lib/motion";

const fade = (d) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: EASE, delay: INTRO_DELAY + d },
});

const HeroMeta = () => (
  <div className="mt-16 grid grid-cols-1 gap-10 border-t border-ink/15 pt-6 md:grid-cols-12 lg:mt-24">
    <motion.p {...fade(0.5)} data-testid="hero-subtext" className="max-w-xl text-lg leading-relaxed md:col-span-6 lg:text-xl">
      {HERO.intro}
    </motion.p>
    <motion.ul {...fade(0.6)} className="space-y-2 text-sm text-mute md:col-span-4 md:col-start-7 lg:col-span-3 lg:col-start-8" data-testid="hero-meta">
      {HERO.meta.map((m) => (
        <li key={m} className="flex gap-3">
          <span className="text-ash" aria-hidden="true">—</span>
          {m}
        </li>
      ))}
    </motion.ul>
    <motion.div {...fade(0.7)} className="md:col-span-2 md:col-start-11 md:text-right">
      <a
        href="#work"
        onClick={(e) => {
          e.preventDefault();
          scrollToTarget("#work");
        }}
        data-testid="hero-explore-link"
        className="group inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium md:flex-col md:items-end"
      >
        <span className="u-link">Explore selected work</span>
        <span className="inline-block transition-transform duration-500 group-hover:translate-y-1">↓</span>
      </a>
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 160]);
  const rotateX = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 14]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, reduce ? 1 : 0.15]);
  return (
    <section ref={ref} className="px-6 pb-16 pt-14 lg:px-12 lg:pb-24 lg:pt-20" aria-labelledby="hero-title">
      <motion.div {...fade(0.2)} className="label mb-10 flex flex-wrap justify-between gap-x-6 gap-y-2 text-mute lg:mb-14">
        <span>Brand &amp; Multidisciplinary Designer</span>
        <UkClock />
      </motion.div>
      <div style={{ perspective: 1200 }}>
        <motion.h1
          id="hero-title"
          data-testid="hero-headline"
          style={{ y, rotateX, opacity, transformOrigin: "50% 100%" }}
          className="font-display text-[clamp(3.3rem,7.4vw,9.5rem)] font-medium leading-[0.92] tracking-[-0.055em]"
        >
          <span className="sr-only">Designing identities, ideas &amp; experiences.</span>
          <span aria-hidden="true" className="flex flex-wrap lg:gap-x-[0.25em]">
            <MaskedLines lines={HERO.lines.slice(0, 2)} delay={INTRO_DELAY} className="w-full lg:flex lg:gap-x-[0.25em]" />
            <MaskedLines lines={HERO.lines.slice(2)} delay={INTRO_DELAY + 0.18} className="w-full lg:flex lg:gap-x-[0.25em] lg:pl-[10vw]" />
          </span>
        </motion.h1>
      </div>
      <HeroMeta />
    </section>
  );
};
